// Shrinks photo/scan uploads before they are sent for marking. Gemini bills images by
// media resolution, not bytes, so this mainly cuts upload time and keeps batches under the
// server limit. 2000px on the long side keeps dense handwriting legible (~170 dpi on A4).
const COMPRESSIBLE_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);
const SMALL_ENOUGH_BYTES = 400 * 1024;

export async function compressImageForUpload(file, { maxDimension = 2000, quality = 0.85 } = {}) {
  if (!file || !COMPRESSIBLE_TYPES.has(file.type) || typeof createImageBitmap !== "function" || typeof document === "undefined") {
    return file;
  }
  let bitmap = null;
  try {
    // "from-image" applies the phone's EXIF rotation, so sideways photos are sent upright.
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    const scale = Math.min(1, maxDimension / Math.max(bitmap.width, bitmap.height));
    if (scale === 1 && file.size <= SMALL_ENOUGH_BYTES) return file;
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const context = canvas.getContext?.("2d");
    if (!context || typeof canvas.toBlob !== "function") return file;
    // JPEG has no alpha: paint white first so transparent PNG scans do not turn black.
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
    if (!blob || (scale === 1 && blob.size >= file.size)) return file;
    const name = file.name.replace(/\.[^.]+$/, "") + ".jpg";
    return new File([blob], name, { type: "image/jpeg", lastModified: file.lastModified });
  } catch {
    return file;
  } finally {
    bitmap?.close?.();
  }
}
